/*
 * (c) Meta Platforms, Inc. and its affiliates.
 */

import React from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { MetadaDataCardData } from "../maps/MapVizClusters";

interface GridDataPoint {
	id: string;
	[key: string]: string | number | any;
}

interface VirtualizedGridProps {
	data: GeoJSON.Feature<GeoJSON.Geometry>[];
	setModalOpen: (e: boolean) => void;
	setModalData: (data: MetadaDataCardData) => void;
}

const VirtualizedGrid = ({
	data,
	setModalOpen,
	setModalData,
}: VirtualizedGridProps) => {
	const parentRef = React.useRef<HTMLDivElement>(null!);
	const [gridData, setGridData] = React.useState<Map<string, GridDataPoint>>();

	React.useEffect(() => {
		let newSet = data?.reduce(
			(map: Map<string, GridDataPoint>, point: any, i: number) => {
				const gridPos = i % 2 === 0 ? `${i / 2}-0` : `${Math.floor(i / 2)}-1`;

				map.set(gridPos, {
					id: gridPos,
					...point.properties,
				});
				return map;
			},
			new Map<string, GridDataPoint>()
		);
		setGridData(newSet);
	}, [data]);

	const rowVirtualizer = useVirtualizer({
		count: Math.ceil(data?.length / 2),
		getScrollElement: () => parentRef.current,
		estimateSize: () => 185,
		overscan: 5,
	});

	const columnVirtualizer = useVirtualizer({
		horizontal: true,
		count: 2,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 185,
		overscan: 5,
	});

	return (
		<>
			<div
				ref={parentRef}
				className="px-2 w-[400px] h-[calc(300px_-_90px)] md:h-[calc(96vh_-_60px)] overflow-y-auto"
			>
				<div
					style={{
						margin: "auto",
						height: `${rowVirtualizer.getTotalSize()}px`,
						width: `${columnVirtualizer.getTotalSize()}px`,
						position: "relative",
					}}
				>
					{rowVirtualizer.getVirtualItems().map((virtualRow) => (
						<React.Fragment key={virtualRow.index}>
							{columnVirtualizer.getVirtualItems().map((virtualColumn) => (
								<div
									key={virtualColumn.index}
									className="p-1"
									style={{
										position: "absolute",
										top: 0,
										left: 0,
										width: `${virtualColumn.size}px`,
										height: `${virtualRow.size}px`,
										transform: `translateX(${virtualColumn.start}px) translateY(${virtualRow.start}px)`,
									}}
								>
									<div className="wrapper mx-1 cursor-pointer">
										<img
											src={
												gridData?.get(
													`${virtualRow.index}-${virtualColumn.index}`
												)?.url as string
											}
											alt=""
											onClick={() => {
												setModalData(
													gridData?.get(
														`${virtualRow.index}-${virtualColumn.index}`
													) as MetadaDataCardData
												);
												setModalOpen(true);
											}}
										/>
									</div>
								</div>
							))}
						</React.Fragment>
					))}
				</div>
			</div>
		</>
	);
};

export default VirtualizedGrid;
